import { useEffect } from 'react';

import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import type { User } from '@sentry/react';

import * as Sentry from '@sentry/react';
import { z } from 'zod';

import { PAYMENT_REQUIRED_STATUS_CODE } from '@/shared/api/errors';
import { isOfType } from '@/shared/lib/validators/isOfType';

import { axiosSentryIntegration } from './lib/axios';
import { isButtonElement } from './lib/utils/isButtonElement';

const originalExceptionStatusSchema = z.object({ status: z.number() });

const ERRORS: Record<PropertyKey, string> = {
  [PAYMENT_REQUIRED_STATUS_CODE]: '402_no_credits_available',
};

export const SentryRask = {
  initIfAvailable: () => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      axiosSentryIntegration();
      Sentry.init({
        beforeBreadcrumb: (breadcrumb, hint) => {
          if (breadcrumb.category?.includes('redux')) {
            return null;
          }

          if (breadcrumb.category?.startsWith('ui') && isButtonElement(hint)) {
            breadcrumb.message = `Button: ${hint?.event.target.textContent}`;
          }

          return breadcrumb;
        },
        beforeSend: (event, { originalException }) => {
          if (
            event.type !== 'message' &&
            isOfType(originalException, originalExceptionStatusSchema) &&
            ERRORS[originalException.status]
          ) {
            Sentry.captureMessage(ERRORS[originalException.status]);
            return null;
          }

          return event;
        },
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENV,
        integrations: [
          Sentry.breadcrumbsIntegration({
            console: false,
          }),
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            networkDetailAllowUrls: ['https://rst.rask.ai/api/library/v1/media'],
            networkRequestHeaders: ['X-Custom-Header'],
            networkResponseHeaders: ['X-Custom-Header'],
          }),
          Sentry.reactRouterV6BrowserTracingIntegration({
            matchRoutes,
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
          }),
          Sentry.captureConsoleIntegration({
            levels: ['error'],
          }),
        ],
        normalizeDepth: 10,
        replaysOnErrorSampleRate: 1,
      });
    }
  },
  initUser: (user: User) => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser(user);
    }
  },
  setTag: Sentry.setTag,
};
